/**
 * Imports Card
 *
 * Displays a list of CSV imports with status badges and allows
 * creating new imports or reviewing existing ones.
 */

import { AlertCircle, Download03, Plus } from '@untitledui/icons';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { useCanWrite } from '@/hooks/usePermissions';
import { Badge } from '@/lib/base/badges/badges';
import { ButtonList } from '@/lib/base/button-list';
import { Button } from '@/lib/base/buttons/button';
import { EmptyState } from '@/lib/base/empty-state';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { CARD_BORDER } from '@/lib/base/list-card';
import {
  type CsvImportListItem,
  type CsvImportStatus,
  listCsvImports,
} from '@/lib/csv-import/api';
import { cx } from '@/utils/cx';

import { CsvImportWizard } from './CsvImportWizard';
import { ReviewModal } from './ReviewModal';

// ============================================================================
// ImportListHeader
// ============================================================================

interface ImportListHeaderProps {
  showCta: boolean;
  onNewImport: () => void;
}

function ImportListHeader({ showCta, onNewImport }: ImportListHeaderProps) {
  return (
    <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4'>
      <h3 className='text-sm font-medium text-fg-primary'>Import files</h3>
      {showCta && (
        <Button
          size='sm'
          color='primary'
          iconLeading={Plus}
          onClick={onNewImport}
          className='self-end sm:self-auto'
        >
          New import
        </Button>
      )}
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function getStatusBadgeProps(status: CsvImportStatus): {
  color: 'brand' | 'success' | 'error' | 'warning' | 'gray';
  label: string;
} {
  switch (status) {
    case 'analyzing':
      return { color: 'brand', label: 'Analyzing' };
    case 'ready_for_review':
      return { color: 'warning', label: 'Ready for Review' };
    case 'importing':
      return { color: 'brand', label: 'Importing' };
    case 'completed':
      return { color: 'success', label: 'Completed' };
    case 'failed':
      return { color: 'error', label: 'Failed' };
    default:
      return { color: 'gray', label: status };
  }
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ============================================================================
// ImportRow
// ============================================================================

interface ImportRowProps {
  importItem: CsvImportListItem;
  onSelect: (item: CsvImportListItem) => void;
}

function ImportRow({ importItem, onSelect }: ImportRowProps) {
  const statusBadge = getStatusBadgeProps(importItem.status);
  const hasResults = importItem.execution_result !== null;
  const handleClick = useCallback(
    () => onSelect(importItem),
    [onSelect, importItem]
  );

  return (
    <ButtonList.Item onAction={handleClick}>
      <div className='flex items-center gap-2'>
        <span className='truncate font-medium text-fg-primary'>
          {importItem.name}
        </span>
        <Badge color={statusBadge.color} size='sm'>
          {statusBadge.label}
        </Badge>
      </div>
      <div className='mt-1 flex items-center gap-3 text-sm text-fg-tertiary'>
        <span>{formatDate(importItem.created_at)}</span>
        {hasResults && importItem.execution_result && (
          <span>
            {importItem.execution_result.created} created,{' '}
            {importItem.execution_result.updated} updated
          </span>
        )}
      </div>
    </ButtonList.Item>
  );
}

// ============================================================================
// Component
// ============================================================================

export function ImportsCard() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const canWrite = useCanWrite();

  // Data state
  const [imports, setImports] = useState<CsvImportListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Wizard state
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  // When set, the wizard opens at the Mapping step with the existing
  // import's data pre-populated so the user can re-map columns.
  const [resumeImportId, setResumeImportId] = useState<number | null>(null);

  // Review modal state — store the full list item so we can hand its
  // status to ReviewModal for correct initial sizing (otherwise the
  // modal would open wide and shrink once load resolves).
  const [selectedImport, setSelectedImport] =
    useState<CsvImportListItem | null>(null);

  // Load imports
  const loadImports = useCallback(async () => {
    if (!orgId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await listCsvImports(orgId);
      setImports(data);
    } catch (err) {
      console.error('Failed to load imports:', err);
      setError("We couldn't load your imports. Try again.");
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    loadImports();
  }, [loadImports]);

  // Handlers
  const handleNewImport = useCallback(() => {
    setIsWizardOpen(true);
  }, []);

  const handleWizardClose = useCallback(() => {
    setIsWizardOpen(false);
    setResumeImportId(null);
    // Refresh list after wizard closes
    loadImports();
  }, [loadImports]);

  const handleImportClick = useCallback((importItem: CsvImportListItem) => {
    setSelectedImport(importItem);
  }, []);

  const handleReviewClose = useCallback(() => {
    setSelectedImport(null);
    // Refresh list in case status changed
    loadImports();
  }, [loadImports]);

  // "Edit Mappings" from ReviewModal: close the review, then open the
  // wizard in resume mode so the user can re-map columns and resubmit.
  const handleEditMappings = useCallback(() => {
    if (!selectedImport) {
      return;
    }
    const importIdToResume = selectedImport.id;
    setSelectedImport(null);
    setResumeImportId(importIdToResume);
    setIsWizardOpen(true);
  }, [selectedImport]);

  // Content for the current state (loading / error / empty / list).
  let content: ReactNode;
  if (loading) {
    content = (
      <div className={cx(CARD_BORDER, 'bg-bg-secondary')}>
        <div className='flex items-center justify-center py-12'>
          <LoadingIndicator size='md' />
        </div>
      </div>
    );
  } else if (error) {
    content = (
      <div className={cx(CARD_BORDER, 'bg-bg-secondary')}>
        <EmptyState
          icon={
            <FeaturedIcon
              icon={AlertCircle}
              size='lg'
              theme='light-outline'
              color='error'
            />
          }
          title="We couldn't load your imports"
          description={error}
          action={{ label: 'Retry', onClick: loadImports }}
        />
      </div>
    );
  } else if (imports.length === 0) {
    content = (
      <div className={cx(CARD_BORDER, 'bg-bg-secondary')}>
        <EmptyState
          icon={
            <FeaturedIcon
              icon={Download03}
              size='lg'
              theme='light-outline'
              color='gray'
            />
          }
          title='No imports yet'
          description='Import contacts, companies, or deals from CSV or Excel files into your CRM.'
          action={
            canWrite
              ? {
                  label: 'New import',
                  onClick: handleNewImport,
                  icon: Plus,
                  color: 'primary',
                }
              : undefined
          }
        />
      </div>
    );
  } else {
    content = (
      <ButtonList aria-label='Import files'>
        {imports.map(importItem => (
          <ImportRow
            key={importItem.id}
            importItem={importItem}
            onSelect={handleImportClick}
          />
        ))}
      </ButtonList>
    );
  }

  return (
    <>
      <div>
        <ImportListHeader showCta={canWrite} onNewImport={handleNewImport} />
        {content}
      </div>

      {/* CSV Import Wizard */}
      {orgId && (
        <CsvImportWizard
          orgId={orgId}
          isOpen={isWizardOpen}
          onClose={handleWizardClose}
          resumeImportId={resumeImportId}
        />
      )}

      {/* Review Modal */}
      {orgId && selectedImport !== null && (
        <ReviewModal
          orgId={orgId}
          importId={selectedImport.id}
          initialStatus={selectedImport.status}
          isOpen={selectedImport !== null}
          onClose={handleReviewClose}
          onEditMappings={handleEditMappings}
        />
      )}
    </>
  );
}
